import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Circle, Camera, Calendar, Clock, User, Sparkles, AlertTriangle, FileText, CheckSquare, ZoomIn, X, Wallet, Home, CalendarClock, Loader } from 'lucide-react';
import { MobileLayout } from '../../components/MobileLayout';
import { CircularScore } from '../../components/LastCleaningWidget';
import { Tabs } from '../../components/ui/Tabs';
import { useScreenInit } from '../../useScreenInit.js';
import { getApartmentById, ApartmentIntervention } from './appartamentiData';
// Mock before/after photos shared across interventions (decorative)
const photosData = [{
  id: 'soggiorno',
  name: 'Soggiorno',
  before: 'https://cdn.magicpatterns.com/uploads/3j17u41pZZqqZ7uBe8PFm8/_livingroom_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/dVhw2cGTmUhE4Ap2uCd1sm/_livingroom_clean.png'
}, {
  id: 'cucina',
  name: 'Cucina',
  before: 'https://cdn.magicpatterns.com/uploads/jUD5ZBDVxzRXjmRX6focQF/_kitchen_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/q7HEF8xL4ZfPHBQejbrvyX/_kitchen_clean.png'
}, {
  id: 'bagno',
  name: 'Bagno',
  before: 'https://cdn.magicpatterns.com/uploads/cK1fhi1rXfQ9v7iDq72qZ1/_bathroom_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/9cNyWn1us7ZSVXzYMKkr7V/_bathroom_clean.png'
}, {
  id: 'camera',
  name: 'Camera',
  before: 'https://cdn.magicpatterns.com/uploads/s9wFmfCssxKKstfcmFWPxC/_bedroom_messy.png',
  after: 'https://cdn.magicpatterns.com/uploads/vL5vGhBPD2XELKb3m3514D/_bedroom_clean.png'
}];
interface ChecklistItem {
  task: string;
  done: boolean;
  isDesiderata: boolean;
}
const checklistData: Record<string, ChecklistItem[]> = {
  Generale: [{
    task: 'Spazzatura svuotata',
    done: true,
    isDesiderata: false
  }, {
    task: 'Finestre pulite',
    done: true,
    isDesiderata: false
  }, {
    task: 'Lenzuola sempre stirate',
    done: true,
    isDesiderata: true
  }],
  Soggiorno: [{
    task: 'Pavimento aspirato e lavato',
    done: true,
    isDesiderata: false
  }, {
    task: 'Superfici spolverate',
    done: true,
    isDesiderata: false
  }, {
    task: 'Cuscini divano sistemati',
    done: true,
    isDesiderata: true
  }],
  Cucina: [{
    task: 'Piano cottura pulito',
    done: true,
    isDesiderata: false
  }, {
    task: 'Elettrodomestici lucidati',
    done: true,
    isDesiderata: false
  }, {
    task: 'Pavimento lavato',
    done: true,
    isDesiderata: false
  }],
  Bagno: [{
    task: 'Sanitari igienizzati',
    done: true,
    isDesiderata: false
  }, {
    task: 'Specchio pulito',
    done: true,
    isDesiderata: false
  }, {
    task: 'Asciugamani piegati a cigno',
    done: true,
    isDesiderata: true
  }],
  Camera: [{
    task: 'Letto rifatto con cura',
    done: true,
    isDesiderata: false
  }, {
    task: 'Pavimento aspirato e lavato',
    done: true,
    isDesiderata: false
  }, {
    task: 'Lenzuola sempre stirate',
    done: true,
    isDesiderata: true
  }]
};
const roomNotes: Record<string, string> = {
  Bagno: 'Silicone doccia da rifare, segnalato al referente.'
};
type StatusMeta = {
  label: string;
  subtitle: string;
  badgeClass: string;
  icon: React.ReactNode;
};
const statusMetaByStatus: Record<ApartmentIntervention['status'], StatusMeta> = {
  Completata: {
    label: 'Completata',
    subtitle: 'Intervento eseguito',
    badgeClass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    icon: <CheckCircle size={12} data-id="element-2429" />
  },
  'In corso': {
    label: 'In corso',
    subtitle: 'Intervento in corso in questo momento',
    badgeClass: 'bg-coral/10 text-coral border-coral/20',
    icon: <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" data-id="element-2430" />
  },
  Pianificata: {
    label: 'Pianificata',
    subtitle: 'Intervento programmato',
    badgeClass: 'bg-muted text-muted-foreground border-border',
    icon: <CalendarClock size={12} data-id="element-2431" />
  }
};
export function CmInterventoDettaglioPage() {
  useScreenInit();
  const {
    id,
    interventoId
  } = useParams<{
    id: string;
    interventoId: string;
  }>();
  const [activeTab, setActiveTab] = useState('overview');
  const [zoomedPhoto, setZoomedPhoto] = useState<{
    src: string;
    alt: string;
  } | null>(null);
  useEffect(() => {
    if (!zoomedPhoto) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setZoomedPhoto(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomedPhoto]);
  const apartment = getApartmentById(id);
  const intervento = apartment?.interventions.find(i => i.id === interventoId);
  if (!apartment || !intervento) {
    return <MobileLayout data-id="element-2432">
        <div className="text-center py-20" data-id="element-2433">
          <p className="text-sm text-muted-foreground mb-4" data-id="element-2434">
            Intervento non trovato.
          </p>
          <Link to={apartment ? `/cm/clienti/appartamenti/${apartment.id}` : '/cm/clienti/appartamenti'} className="inline-flex items-center gap-1.5 text-sm font-medium text-coral hover:underline" data-id="element-2435">
            <ArrowLeft size={15} data-id="element-2436" />
            Torna all'appartamento
          </Link>
        </div>
      </MobileLayout>;
  }
  const statusMeta = statusMetaByStatus[intervento.status];
  const isCompleted = intervento.status === 'Completata';
  const isInProgress = intervento.status === 'In corso';
  const isPlanned = intervento.status === 'Pianificata';
  const allTasks = Object.values(checklistData).flat();
  const completedTasks = allTasks.filter(t => t.done).length;
  const totalTasks = allTasks.length;
  const inProgressDone = Math.round(totalTasks * 0.45);
  const cleanerInitials = intervento.cleaner.split(' ').map(p => p[0]).join('');
  const renderChecklistItem = (item: ChecklistItem, idx: number) => {
    const done = isPlanned ? false : item.done;
    return <li key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100" data-id="element-2437">
        <div className="mt-0.5 flex-shrink-0" data-id="element-2438">
          {done ? <CheckCircle size={18} className="text-emerald-500" data-id="element-2439" /> : <Circle size={18} className="text-muted-foreground" data-id="element-2440" />}
        </div>
        <div className="flex-1" data-id="element-2441">
          <span className={`text-sm ${done ? 'text-foreground' : 'text-muted-foreground'}`} data-id="element-2442">
            {item.task}
          </span>
          {item.isDesiderata && <div className="flex items-center gap-1 mt-1.5" data-id="element-2443">
              <Sparkles size={12} className="text-muted-foreground" data-id="element-2444" />
              <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider" data-id="element-2445">
                Desiderata Host
              </span>
            </div>}
        </div>
      </li>;
  };
  return <MobileLayout data-id="element-2446">
      {/* Back to apartment */}
      <Link to={`/cm/clienti/appartamenti/${apartment.id}`} className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-navy transition-colors mb-4" data-id="element-2447">
        <ArrowLeft size={16} data-id="element-2448" />
        {apartment.name}
      </Link>

      {/* Header */}
      <div className="mb-6" data-id="element-2449">
        <div className="flex items-center gap-2 flex-wrap" data-id="element-2450">
          <h1 className="text-xl lg:text-2xl font-bold text-foreground" data-id="element-2451">
            {intervento.type}
          </h1>
          <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full border ${statusMeta.badgeClass}`} data-id="element-2452">
            {statusMeta.icon}
            {statusMeta.label}
          </span>
        </div>
        <p className="text-xs lg:text-sm text-muted-foreground mt-0.5" data-id="element-2453">
          {statusMeta.subtitle} · {apartment.address} · {apartment.zone}
        </p>
      </div>

      {/* Top info bar — cleaner identity + score */}
      <div className="bg-white border border-border rounded-xl mb-6 shadow-sm w-full" data-id="element-2454">
        <div className="flex items-center justify-between gap-4 p-5" data-id="element-2455">
          <div className="flex items-center gap-3 min-w-0" data-id="element-2456">
            <span className="w-11 h-11 rounded-full bg-coral/10 flex items-center justify-center text-sm font-bold text-coral flex-shrink-0" data-id="element-2457">
              {cleanerInitials}
            </span>
            <div className="min-w-0" data-id="element-2458">
              <div className="text-[10px] text-muted-foreground uppercase tracking-wider font-medium" data-id="element-2459">
                Cleaner
              </div>
              <div className="text-sm font-semibold text-foreground truncate" data-id="element-2460">
                {intervento.cleaner}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0" data-id="element-2461">
            {isCompleted && intervento.score !== null && <CircularScore score={intervento.score} data-id="element-2462" />}
            {isInProgress && <div className="flex flex-col items-end" data-id="element-2463">
                <span className="inline-flex items-center gap-1.5 text-xs font-bold text-coral bg-coral/10 border border-coral/20 px-2.5 py-1 rounded-full" data-id="element-2464">
                  <span className="w-1.5 h-1.5 rounded-full bg-coral animate-pulse" data-id="element-2465" />
                  In corso
                </span>
                <span className="text-[11px] text-muted-foreground mt-1" data-id="element-2466">
                  {inProgressDone}/{totalTasks} task
                </span>
              </div>}
            {isPlanned && <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground bg-muted border border-border px-2.5 py-1 rounded-full" data-id="element-2467">
                <CalendarClock size={14} data-id="element-2468" />
                Pianificata
              </span>}
          </div>
        </div>

        <div className="border-t border-border" data-id="element-2469" />

        <div className="grid grid-cols-2 sm:flex sm:flex-wrap sm:items-center gap-x-5 gap-y-3 p-5" data-id="element-2470">
          <div className="flex items-center gap-2 min-w-0" data-id="element-2471">
            <Calendar size={15} className="text-coral flex-shrink-0" data-id="element-2472" />
            <span className="text-sm font-medium text-foreground truncate" data-id="element-2473">
              {intervento.date}
            </span>
          </div>
          <span className="hidden sm:inline text-border" data-id="element-2474">·</span>
          <div className="flex items-center gap-2 min-w-0" data-id="element-2475">
            <Home size={15} className="text-coral flex-shrink-0" data-id="element-2476" />
            <span className="text-sm font-medium text-foreground truncate" data-id="element-2477">
              {apartment.name} · {apartment.type}
            </span>
          </div>
          <span className="hidden sm:inline text-border" data-id="element-2478">·</span>
          <div className="flex items-center gap-2 min-w-0 col-span-2 sm:col-auto" data-id="element-2479">
            <Wallet size={15} className="text-coral flex-shrink-0" data-id="element-2480" />
            <span className="text-sm font-medium text-foreground truncate" data-id="element-2481">
              Guadagno €{intervento.earning}
            </span>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <Tabs className="mb-2 -mx-4 px-4 lg:mx-0 lg:px-0" scrollable value={activeTab} onValueChange={setActiveTab} items={[{
      id: 'overview',
      label: 'Overview'
    }, ...photosData.map(room => ({
      id: room.id,
      label: room.name
    }))]} data-id="element-2482" />

      <div className="w-full space-y-6" data-id="element-2483">
        {/* OVERVIEW */}
        {activeTab === 'overview' && <>
            {/* Summary stats */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-2484">
              <h2 className="text-base font-bold text-foreground mb-4" data-id="element-2485">
                Riepilogo intervento
              </h2>
              <div className="grid grid-cols-3 gap-4 divide-x divide-border" data-id="element-2486">
                {isCompleted && <>
                    <div className="text-center px-2" data-id="element-2487">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2488">
                        {completedTasks}/{totalTasks}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2489">
                        Task completati
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-2490">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2491">
                        {intervento.score ?? '—'}
                        <span className="text-sm text-muted-foreground font-normal" data-id="element-2492">
                          /100
                        </span>
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2493">
                        Punteggio qualità
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-2494">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2495">
                        €{intervento.earning}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2496">
                        Guadagno
                      </div>
                    </div>
                  </>}
                {isInProgress && <>
                    <div className="text-center px-2" data-id="element-2497">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2498">
                        {inProgressDone}/{totalTasks}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2499">
                        Task completati
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-2500">
                      <div className="inline-flex items-center gap-1.5 text-base font-bold text-coral" data-id="element-2501">
                        <Loader size={18} className="animate-spin" data-id="element-2502" />
                        Live
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2503">
                        Stato
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-2504">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2505">
                        €{intervento.earning}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2506">
                        Guadagno previsto
                      </div>
                    </div>
                  </>}
                {isPlanned && <>
                    <div className="text-center px-2" data-id="element-2507">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2508">
                        {totalTasks}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2509">
                        Task previsti
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-2510">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2511">
                        {intervento.cleaner.split(' ')[0]}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2512">
                        Assegnato a
                      </div>
                    </div>
                    <div className="text-center px-2" data-id="element-2513">
                      <div className="text-2xl font-bold text-foreground" data-id="element-2514">
                        €{intervento.earning}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1" data-id="element-2515">
                        Guadagno previsto
                      </div>
                    </div>
                  </>}
              </div>

              {isInProgress && <div className="mt-4" data-id="element-2516">
                  <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5" data-id="element-2517">
                    <span data-id="element-2518">Avanzamento</span>
                    <span className="font-medium text-coral" data-id="element-2519">
                      {Math.round(inProgressDone / totalTasks * 100)}%
                    </span>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden" data-id="element-2520">
                    <div className="bg-coral h-2 rounded-full transition-all duration-500" style={{
                width: `${inProgressDone / totalTasks * 100}%`
              }} data-id="element-2521" />
                  </div>
                </div>}
            </div>

            {/* Status hints */}
            {isInProgress && <div className="bg-coral/5 border border-coral/20 rounded-xl p-4 flex items-center gap-3" data-id="element-2522">
                <Loader size={18} className="text-coral animate-spin flex-shrink-0" data-id="element-2523" />
                <p className="text-sm text-foreground" data-id="element-2524">
                  Il cleaner sta lavorando. Punteggio CleanAI e foto saranno
                  disponibili al termine.
                </p>
              </div>}
            {isPlanned && <div className="bg-white border border-border rounded-xl p-4 shadow-sm flex items-start gap-3" data-id="element-2525">
                <CalendarClock size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-2526" />
                <p className="text-sm text-muted-foreground" data-id="element-2527">
                  Intervento pianificato. Le attività verranno svolte da{' '}
                  {intervento.cleaner}.
                </p>
              </div>}

            {/* General checklist */}
            <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-2528">
              <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-2529">
                <CheckSquare size={18} className="text-muted-foreground" data-id="element-2530" />
                Checklist generale
              </h2>
              <ul className="space-y-3" data-id="element-2531">
                {checklistData.Generale.map((item, idx) => renderChecklistItem(item, idx))}
              </ul>
            </div>
          </>}

        {/* ROOM TABS */}
        {photosData.map(room => {
        if (activeTab !== room.id) return null;
        const roomTasks = checklistData[room.name] || [];
        const notes = roomNotes[room.name];
        return <div key={room.id} className="space-y-6" data-id="element-2532">
              {isPlanned && <div className="bg-white border border-border rounded-xl p-5 shadow-sm flex items-start gap-3" data-id="element-2533">
                  <CalendarClock size={18} className="text-muted-foreground flex-shrink-0 mt-0.5" data-id="element-2534" />
                  <p className="text-sm text-muted-foreground" data-id="element-2535">
                    Le foto Prima / Dopo saranno disponibili una volta svolto
                    l'intervento.
                  </p>
                </div>}
              {isInProgress && <div className="bg-white border border-border rounded-xl p-5 shadow-sm flex items-start gap-3" data-id="element-2536">
                  <Camera size={18} className="text-coral flex-shrink-0 mt-0.5" data-id="element-2537" />
                  <p className="text-sm text-muted-foreground" data-id="element-2538">
                    Intervento in corso: le foto verranno caricate man mano dal
                    cleaner.
                  </p>
                </div>}
              {isCompleted && <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-2539">
                  <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-2540">
                    <Camera size={18} className="text-muted-foreground" data-id="element-2541" />
                    Foto Prima / Dopo
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-2542">
                    <div className="space-y-2" data-id="element-2543">
                      <div className="inline-block px-3 py-1 bg-amber-50 text-amber-700 text-xs font-bold rounded-md border border-amber-100" data-id="element-2544">
                        Prima
                      </div>
                      <button type="button" onClick={() => setZoomedPhoto({
                  src: room.before,
                  alt: `Prima ${room.name}`
                })} aria-label={`Ingrandisci foto prima ${room.name}`} className="group relative block w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-coral" data-id="element-2545">
                        <img src={room.before} alt={`Prima ${room.name}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-2546" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors" data-id="element-2547">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-foreground rounded-full p-2 shadow-sm" data-id="element-2548">
                            <ZoomIn size={18} data-id="element-2549" />
                          </span>
                        </span>
                      </button>
                    </div>
                    <div className="space-y-2" data-id="element-2550">
                      <div className="inline-block px-3 py-1 bg-emerald-50 text-emerald-700 text-xs font-bold rounded-md border border-emerald-100" data-id="element-2551">
                        Dopo
                      </div>
                      <button type="button" onClick={() => setZoomedPhoto({
                  src: room.after,
                  alt: `Dopo ${room.name}`
                })} aria-label={`Ingrandisci foto dopo ${room.name}`} className="group relative block w-full aspect-[4/3] rounded-lg overflow-hidden border border-border bg-muted cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-coral" data-id="element-2552">
                        <img src={room.after} alt={`Dopo ${room.name}`} className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105" data-id="element-2553" />
                        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/30 transition-colors" data-id="element-2554">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white/90 text-foreground rounded-full p-2 shadow-sm" data-id="element-2555">
                            <ZoomIn size={18} data-id="element-2556" />
                          </span>
                        </span>
                      </button>
                    </div>
                  </div>
                </div>}

              <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-2557">
                <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-4" data-id="element-2558">
                  <CheckSquare size={18} className="text-muted-foreground" data-id="element-2559" />
                  Checklist {room.name}
                  {isPlanned && <span className="text-xs font-normal text-muted-foreground" data-id="element-2560">
                      (da svolgere)
                    </span>}
                </h2>
                <ul className="space-y-3" data-id="element-2561">
                  {roomTasks.map((item, idx) => renderChecklistItem(item, idx))}
                </ul>
              </div>

              {!isPlanned && notes && <div className="bg-white border border-border rounded-xl p-5 shadow-sm" data-id="element-2562">
                  <h2 className="text-base font-bold text-foreground flex items-center gap-2 mb-3" data-id="element-2563">
                    <FileText size={18} className="text-muted-foreground" data-id="element-2564" />
                    Note del cleaner
                  </h2>
                  <div className="p-4 bg-gray-50 rounded-lg border border-gray-100 text-sm text-foreground" data-id="element-2565">
                    {notes}
                  </div>
                </div>}
            </div>;
      })}
      </div>

      {/* Lightbox */}
      {zoomedPhoto && <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setZoomedPhoto(null)} role="dialog" aria-modal="true" aria-label={zoomedPhoto.alt} data-id="element-2566">
          <button type="button" onClick={() => setZoomedPhoto(null)} aria-label="Chiudi" className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors" data-id="element-2567">
            <X size={24} data-id="element-2568" />
          </button>
          <img src={zoomedPhoto.src} alt={zoomedPhoto.alt} onClick={e => e.stopPropagation()} className="max-w-full max-h-[88vh] w-auto h-auto object-contain rounded-lg shadow-2xl cursor-default" data-id="element-2569" />
        </div>}
    </MobileLayout>;
}